'use client';

import { useState } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { RoomInfoPanel } from '@/components/public/room-info-panel';
import { ImageLightbox } from '@/components/public/image-lightbox';
import type { LightboxImage } from '@/components/public/image-lightbox';
import type { Room } from '@/types/database';

export default function Room37Content({ room }: { room: Room }) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const t = useTranslations('roomContent.room-37');

  const GALLERY_IMAGES: LightboxImage[] = [
    { src: '/design-images/uploads/1778227869947-37_ch__4.jpg', alt: t('alt.entrance'), width: 1920, height: 1370 },
    { src: '/design-images/uploads/1778227606202-_mg_7206.jpg', alt: t('alt.wool'), width: 1920, height: 1268 },
    { src: '/design-images/uploads/1778227963872-37_ch_.jpg', alt: t('alt.interior'), width: 1920, height: 1282 },
    { src: '/design-images/uploads/1778227964017-37_ch__2.jpg', alt: t('alt.workspace'), width: 1709, height: 2560 },
    { src: '/design-images/uploads/1778228027119-37_ch__5.jpg', alt: t('alt.bathroom'), width: 1333, height: 1600 },
  ];

  return (
    <div id="room-content" className="w-full flex flex-col">
      {/* White spacer between hero and first section */}
      <div className="bg-white h-2 w-full" />

      {/* Section: Room Benefits */}
      <section className="bg-white w-full">
        <div className="max-w-[1500px] mx-auto w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            <div className="w-full flex items-center py-6 px-6 md:px-8 lg:px-12">
              <div className="text-black w-full">
                <div className="tiptap-content text-base md:text-lg leading-relaxed">
                    <h1 className="font-heading font-bold mb-6 text-4xl md:text-5xl">{t('benefits.title')}</h1>
                    <p className="mb-6 text-gray-700 leading-relaxed">{t('benefits.p1')}</p>
                </div>
                <RoomInfoPanel room={room} />
              </div>
            </div>
            <div
              className="w-full h-full min-h-[400px] md:min-h-[500px] relative cursor-pointer"
              onClick={() => setLightboxIndex(0)}
            >
              <Image
                src="/design-images/uploads/1778227869947-37_ch__4.jpg"
                alt={t('alt.entrance')}
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 100vw, 50vw"
              />
            </div>
          </div>
        </div>
      </section>
      <div className="bg-white h-2 w-full" />

      {/* Section: Side-by-side */}
      <section className="bg-white w-full">

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            <div
              className="w-full h-full min-h-[400px] md:min-h-[500px] relative cursor-pointer"
              onClick={() => setLightboxIndex(1)}
            >
              <Image
                src="/design-images/uploads/1778227606202-_mg_7206.jpg"
                alt={t('alt.wool')}
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 100vw, 50vw"
              />
            </div>
            <div className="w-full flex items-center py-8 md:py-12 px-6 md:px-8 lg:px-12">
              <div className="text-black w-full">
                <div className="tiptap-content text-base md:text-lg leading-relaxed">
                    <h1 className="font-heading font-bold mb-6 text-4xl md:text-5xl">{t('story.titleLine1')}<br /><strong>{t('story.titleLine2')}</strong></h1>
                    <div className="w-16 h-1 bg-[#801515] mb-8" />
                    <p className="text-lg md:text-xl leading-relaxed font-light italic text-gray-700">{t('story.p1')}</p>
                    <p className="text-lg md:text-xl leading-relaxed font-light text-gray-700">{t('story.p2')}</p>
                </div>
              </div>
            </div>
        </div>

      </section>
      <div className="bg-white h-2 w-full" />

      {/* Paired images */}
      <section className="bg-white w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
          <div className="cursor-pointer" onClick={() => setLightboxIndex(2)}>
            <Image
              src="/design-images/uploads/1778227963872-37_ch_.jpg"
              alt={t('alt.interior')}
              width={1920}
              height={1282}
              className="w-full h-auto"
              sizes="(max-width: 1024px) 100vw, 50vw"
            />
          </div>
          <div className="cursor-pointer" onClick={() => setLightboxIndex(3)}>
            <Image
              src="/design-images/uploads/1778227964017-37_ch__2.jpg"
              alt={t('alt.workspace')}
              width={1709}
              height={2560}
              className="w-full h-auto"
              sizes="(max-width: 1024px) 100vw, 50vw"
            />
          </div>
        </div>
      </section>
      <div className="bg-white h-2 w-full" />

      {/* Final portrait image */}
      <section className="bg-white w-full">
        <div className="flex justify-center py-6 px-6 md:px-8 lg:px-12">
          <div className="w-full max-w-[400px] cursor-pointer" onClick={() => setLightboxIndex(4)}>
            <Image
              src="/design-images/uploads/1778228027119-37_ch__5.jpg"
              alt={t('alt.bathroom')}
              width={1333}
              height={1600}
              className="w-full h-auto"
              sizes="(max-width: 768px) 85vw, 400px"
            />
          </div>
        </div>
      </section>

      {/* Fullscreen gallery lightbox */}
      {lightboxIndex !== null && (
        <ImageLightbox
          images={GALLERY_IMAGES}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
}
